<template>
  <vue-final-modal
    :value="visible"
    name="yagiprimefontpayment-dialog"
    :classes="['yagiprime-font-payment-dialog__container']"
    :content-class="[
      'yagiprime-font-payment-dialog__content',
      'v--modal-box',
      'v--modal'
    ]"
    :click-to-close="true"
    :esc-to-close="true"
    :lock-scroll="true"
    attach="body"
    @input="onVisibleInput"
  >
    <div
      class="v--modal-close-top"
      v-loading.fullscreen.lock="fullscreenLoading"
    >
      <el-button
        @click="closeFontpaymentDialog"
        icon="el-icon-circle-close"
        style="font-size: 2rem"
        type="text"
      ></el-button>
    </div>

    <div class="setting" v-if="permissionType == 1">
      <div class="prime-font-popup">
        <h2 class="p-h2">ヤギプライム登録でフォントを切り替え</h2>
        <ul class="pc-list pc-list01">
          <li class="renew2_flex renew2_sp_flex pc-item">
            <p class="item-left">フォント利用</p>
            <p class="item-right">ヤギプライム加入特典</p>
          </li>
          <li
            v-if="isCampaign && paymentMethodTab == 'card'"
            class="renew2_flex renew2_sp_flex pc-item"
          >
            <p class="item-left">お支払い金額（税込）</p>
            <p
              class="item-right item-amount"
              style="text-decoration: line-through; margin-bottom: 0"
            >
              <span>月額 </span>550<span>円</span>
            </p>
            <p class="item-right item-amount">
              <span>7日間 </span>0<span>円</span>
            </p>
          </li>
          <li v-else class="renew2_flex renew2_sp_flex pc-item">
            <p class="item-left">お支払い金額（税込）</p>
            <p class="item-right item-amount">
              <span>月額 </span>550<span>円</span>
            </p>
          </li>
          <li class="pc-item">
            <p class="item-txt pc-memo">
              ※Yagishの<a href="/terms" target="_blank" class="pc-attention"
                >利用規約</a
              >、<a
                href="https://www.yagish.jp/privacy/"
                target="_blank"
                class="pc-attention"
                >個人情報保護方針</a
              >、ヤギプライムの<a
                href="/yagiprimeterms"
                target="_blank"
                class="pc-attention"
                >利用規約</a
              >をご確認ください。
            </p>
            <p class="item-txt pc-memo">
              ※フォントの利用はヤギプライム登録期間中のみになります。ヤギプライム解除後はフォントの利用ができなくなるのでご注意ください。
            </p>
            <p class="item-txt pc-memo">
              ※ヤギプライムについての説明は<a
                href="/yagiprimeinfo"
                target="_blank"
                class="pc-attention"
                >こちら</a
              >をご確認ください。
            </p>
          </li>
        </ul>
        <PaymentForm
          :serviceName="serviceName"
          :price="price"
          :is_subscription="true"
        />
      </div>
    </div>
    <div class="setting" v-else-if="permissionType == 2">
      <div class="setting-item" style="text-align: center">
        <p style="font-size: 150%">ヤギプライムサービス購入完了</p>
        <div style="display: inline-block; min-width: 100%; text-align: center">
          <p style="font-size: 140%"></p>
        </div>
        <p class="thanks-txt">ヤギプライム会員にご登録ありがとうございます！</p>
        <p style="text-align: left" class="note-txt">
          ヤギプライムへのお支払いが完了しました。早速、ヤギプライムの機能を使ってみましょう！
        </p>
        <ul style="text-align: left; font-size: 0.7rem; margin-left: 0">
          <li>
            ※フォントの切り替え方法については<a
              href="/fontservicehelp"
              target="_blank"
              >こちら</a
            >をご確認ください。
          </li>
        </ul>
      </div>
    </div>
    <div class="text-right">
      <el-button @click="closeFontpaymentDialog">閉じる</el-button>
    </div>
  </vue-final-modal>
</template>

<script setup lang="ts">
/**
 * ヤギプライム・フォント課金ダイアログ（vue-final-modal）。
 * open は mitt（open-fontpayment-dialog / open-yagiprimefontpayment-dialog）。
 * 閉じた通知は mitt → hub forward（FormatEdit / MyPage の $on 互換）。
 */
import axios from 'axios'
import {
  getCurrentInstance,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref
} from 'vue'
import { useEventListener } from '@vueuse/core'
import { useAuth } from '@/composables/useAuth'
import { emitter } from '@/plugins/emitter'
import PaymentForm from '@/components/Payment/PaymentMethodSelector.vue'

const OPEN_FONTPAYMENT_DIALOG = 'open-fontpayment-dialog'
const OPEN_YAGIPRIME_FONTPAYMENT_DIALOG = 'open-yagiprimefontpayment-dialog'
const FONTPAYMENT_DIALOG_CLOSED = 'yagiprimefontpayment-dialog-closed'
const PAYMENT_YAGIPRIME_RESULT = 'payment_yagiprime_result'
const CLOSE_YAGIPRIME_FONTPAYMENT_DIALOG = 'close-yagiprimefontpayment-dialog'

type HostVm = {
  $deviceLib: { isMobileDevice: boolean }
  $site: { appFlag: string }
  $varuna: { endPoints: Record<string, string> }
  $message: (opts: Record<string, unknown>) => void
  $globalVar: { isYagiPrime: boolean }
}

const vm = getCurrentInstance()!.proxy as unknown as HostVm
const { user, getClient } = useAuth()

const visible = ref(false)
const formatid = ref('')
const permissionType = ref(1)
const fullscreenLoading = ref(false)
const paymentMethodTab = ref('card')
const isCampaign = ref(false)
const isCampaign4Me = ref(false)
const trialCampaignId = ref<number | null>(0)
const serviceName = ref('ヤギプライム')
const price = ref(550)
const isModalVisible = ref(false)

function currentUid(): string {
  return getClient()?.user?.uid ?? user.value?.uid ?? ''
}

function onVisibleInput(value: boolean) {
  if (value) {
    visible.value = true
    return
  }
  if (!visible.value) return
  closeFontpaymentDialog()
}

function openFontpaymentDialog(id?: unknown) {
  formatid.value = typeof id === 'string' ? id : ''
  visible.value = true
  isModalVisible.value = true
  permissionType.value = 1
  authStateChanged().catch(() => {})

  const formData = new FormData()
  formData.append('uuid', currentUid())
  formData.append('format_id', formatid.value)
  formData.append('dialog_name', 'yagiprimefontpayment_dialog')
  formData.append('permission_type', String(permissionType.value))
  formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
  formData.append('version', '2')
  formData.append('site_flag', 'r')
  formData.append('app_flag', vm.$site.appFlag)
  axios
    .post(vm.$varuna.endPoints.displayDialog, formData, {
      timeout: 60000
    })
    .catch(() => {})
}

function closeFontpaymentDialog() {
  if (!visible.value && !isModalVisible.value) return
  visible.value = false
  isModalVisible.value = false
  emitter.emit(FONTPAYMENT_DIALOG_CLOSED)
}

function handlePaymentResult(result: unknown) {
  fullscreenLoading.value = false
  if (result === 'error') {
    vm.$message({
      message: '決済失敗しました',
      type: 'error'
    })
  } else if (result === 'success' && isModalVisible.value) {
    vm.$message({
      message: '決済完了しました',
      type: 'success'
    })
    location.reload()
  }
}

function onPaymentYagiprimeResult(result: unknown) {
  if (result === 'success') {
    handlePaymentResult('success')
  } else if (result === 'error') {
    handlePaymentResult('error')
  }
}

async function authStateChanged() {
  const uid = currentUid()
  if (!uid) return
  try {
    const response = await axios.get(
      vm.$varuna.endPoints.isSubscribed + uid + '/',
      { timeout: 60000 }
    )
    if (response.data.is_subscribed) {
      vm.$message({
        message: 'すでにヤギプライム登録中です。',
        type: 'success'
      })
      visible.value = false
      isModalVisible.value = false
    }
  } catch {
    // ignore
  } finally {
    fullscreenLoading.value = false
  }

  const formData = new FormData()
  formData.append('uuid', uid)
  try {
    const response = await axios.post(
      vm.$varuna.endPoints.canUseTrialCampaign,
      formData,
      { timeout: 60000 }
    )
    if (response.data.can_use_trial_campaign) {
      isCampaign4Me.value = true
    } else {
      isCampaign4Me.value = false
      isCampaign.value = false
      trialCampaignId.value = null
    }
  } catch {
    // ignore
  } finally {
    fullscreenLoading.value = false
  }
}

function onLoadingOn() {
  fullscreenLoading.value = true
}

function onLoadingOff() {
  fullscreenLoading.value = false
}

useEventListener(window, 'popstate', closeFontpaymentDialog)

onMounted(() => {
  axios
    .get(vm.$varuna.endPoints.getTrialCampaign, { timeout: 60000 })
    .then((response) => {
      const row = response.data?.[0]
      if (!row) return
      const today = new Date()
      const startDate = new Date(row.trial_campaign_start_date)
      const endDate = new Date(row.trial_campaign_end_date)
      if (
        row.trial_campaign_is_active &&
        today >= startDate &&
        today <= endDate
      ) {
        isCampaign.value = true
        trialCampaignId.value = row.trial_campaign_id
      }
    })
    .catch(() => {})
    .finally(() => {
      fullscreenLoading.value = false
    })
})

onBeforeMount(() => {
  emitter.on(OPEN_YAGIPRIME_FONTPAYMENT_DIALOG, openFontpaymentDialog)
  emitter.on(OPEN_FONTPAYMENT_DIALOG, openFontpaymentDialog)
  emitter.on(CLOSE_YAGIPRIME_FONTPAYMENT_DIALOG, closeFontpaymentDialog)
  emitter.on('loading_on', onLoadingOn)
  emitter.on('loading_off', onLoadingOff)
  emitter.on(PAYMENT_YAGIPRIME_RESULT, onPaymentYagiprimeResult)
})

onBeforeUnmount(() => {
  emitter.off(OPEN_YAGIPRIME_FONTPAYMENT_DIALOG, openFontpaymentDialog)
  emitter.off(OPEN_FONTPAYMENT_DIALOG, openFontpaymentDialog)
  emitter.off(CLOSE_YAGIPRIME_FONTPAYMENT_DIALOG, closeFontpaymentDialog)
  emitter.off('loading_on', onLoadingOn)
  emitter.off('loading_off', onLoadingOff)
  emitter.off(PAYMENT_YAGIPRIME_RESULT, onPaymentYagiprimeResult)
})
</script>

<style lang="scss" src="@/assets/scss/pages/pages.scss"></style>
<style lang="scss" scoped>
.prime-font-popup .p-h2 {
  font-size: 16px;
  line-height: 1.4;
  margin-bottom: 10px;
}

.prime-font-popup .pc-list {
  background: #edf5e8;
  border-radius: 5px;
  padding: 15px 20px;
  margin-left: 0;
  margin-bottom: 15px;
}

.prime-font-popup .pc-item {
  justify-content: space-between;
  align-items: flex-end;
}

.prime-font-popup .item-left {
  font-weight: bold;
}

.prime-font-popup .item-amount {
  color: #e3165c;
  font-size: 18px;
  font-weight: 700;
}

.prime-font-popup .item-amount span {
  font-size: 16px;
  text-decoration: none;
}

.prime-font-popup .item-txt {
  font-size: 12px;
}

.prime-font-popup .pc-attention {
  color: red;
}

.prime-font-popup .pc-memo:first-child {
  margin-top: 10px;
}
</style>

<style lang="scss">
// 中央寄せは __container 側（.vfm に :styles で flex を渡すと幅が縮む）。
// align-items:center だと高いコンテンツの上が切れスクロール不能になるため、
// flex-start + content の margin-block:auto で短いときだけ縦中央にする。
.yagiprime-font-payment-dialog__container {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 20px 16px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  z-index: 1001;
}

.yagiprime-font-payment-dialog__content.v--modal-box.v--modal {
  position: relative;
  box-sizing: border-box;
  flex: 0 0 auto;
  width: calc(100% - 32px);
  max-width: 600px;
  height: auto;
  margin-block: auto;
  border-radius: 8px;
  padding: 20px;
}
</style>
